/**
 * Escape на десктопе и в вебе — «назад»: закрывает открытый чат (в двух панелях справа
 * остаётся «Выберите чат»), из профиля возвращает в чат и т. п.
 *
 * <p>Порядок важен. Сначала Escape достаётся открытому окну поверх (`Modal` в вебе сам
 * закрывается по Escape, на keyup), потом — экрану: выйти из выбора сообщений, из правки,
 * снять ответ (`useEscapeInterceptor`). И только если никто его не взял — шаг назад по стеку.
 * Иначе одно нажатие закрывало бы и меню, и чат под ним.
 *
 * <p>Экран звонка Escape не закрывает: случайное нажатие не должно уводить со звонка.
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import { navigationRef } from './navigationRef';

type RootState = ReturnType<typeof navigationRef.getRootState>;

/** Экраны, с которых Escape не уходит. */
const ESCAPE_STAYS_ON = new Set(['ChatList', 'Call']);

/** Есть ли куда отступить по Escape из текущего состояния стека. */
export function canCloseOnEscape(state: RootState | undefined): boolean {
  if (!state || !Array.isArray(state.routes) || state.routes.length < 2) return false;
  const index = state.index ?? state.routes.length - 1;
  if (index < 1) return false;
  const top = state.routes[index];
  return Boolean(top) && !ESCAPE_STAYS_ON.has(top.name);
}

/** Обработчик экрана: вернул true — Escape забран, назад не идём. Последний добавленный — первый. */
type Interceptor = () => boolean;
/**
 * Два яруса. Первый — у полей ввода (поиск в списке слева): курсор в поле — Escape его,
 * даже если справа открыт чат с ответом или правкой. Второй — у экранов.
 */
export type EscapeTier = 'field' | 'screen';
const tiers: Record<EscapeTier, Interceptor[]> = { field: [], screen: [] };

function runInterceptors(): boolean {
  for (const list of [tiers.field, tiers.screen]) {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (list[i]()) return true;
    }
  }
  return false;
}

/** Добавить обработчик Escape; вернёт функцию снятия. */
export function addEscapeInterceptor(handler: Interceptor, tier: EscapeTier = 'screen'): () => void {
  const list = tiers[tier];
  const entry: Interceptor = () => handler();
  list.push(entry);
  return () => {
    const at = list.indexOf(entry);
    if (at >= 0) list.splice(at, 1);
  };
}

/** Уйдёт ли Escape назад по стеку, если его никто не заберёт. */
export function escapeWouldNavigate(): boolean {
  return navigationRef.isReady() && canCloseOnEscape(navigationRef.getRootState() as RootState | undefined);
}

/**
 * Дать экрану первым ответить на Escape, пока `active` (обычно — пока экран в фокусе).
 * `handler` читается на каждое нажатие, его не нужно мемоизировать.
 */
export function useEscapeInterceptor(
  handler: Interceptor,
  active: boolean,
  tier: EscapeTier = 'screen',
): void {
  const ref = useRef(handler);
  // Свежий обработчик после каждой отрисовки: он замыкает текущее состояние экрана.
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!active || Platform.OS !== 'web') return;
    return addEscapeInterceptor(() => ref.current(), tier);
  }, [active, tier]);
}

type KeyEventLike = {
  key?: string;
  repeat?: boolean;
  defaultPrevented?: boolean;
  isComposing?: boolean;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  preventDefault?: () => void;
};

type DocumentLike = { querySelector?: (selector: string) => unknown };

/** Открыто ли окно поверх экрана: веб-`Modal` из react-native-web ставит aria-modal. */
function overlayOpen(doc: DocumentLike | undefined): boolean {
  return Boolean(doc?.querySelector?.('[aria-modal="true"]'));
}

/** Разобрать нажатие; true — Escape отработан (взят экраном или сделан шаг назад). */
export function handleEscapeKey(e: KeyEventLike, doc: DocumentLike | undefined): boolean {
  if (e.key !== 'Escape' || e.repeat || e.defaultPrevented || e.isComposing) return false;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  if (overlayOpen(doc)) return false;
  if (runInterceptors()) {
    e.preventDefault?.();
    return true;
  }
  if (!escapeWouldNavigate()) return false;
  e.preventDefault?.();
  navigationRef.goBack();
  return true;
}

/** Повесить Escape на окно. Только веб: на телефоне клавиши Escape нет, «назад» — свой. */
export function useEscapeToClose(): void {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const target = globalThis as unknown as {
      addEventListener?: (type: string, fn: (e: KeyEventLike) => void, capture: boolean) => void;
      removeEventListener?: (type: string, fn: (e: KeyEventLike) => void, capture: boolean) => void;
      document?: DocumentLike;
    };
    const onKey = (e: KeyEventLike) => {
      handleEscapeKey(e, target.document);
    };
    // Перехват, а не всплытие: TextInput из react-native-web глушит всплытие keydown, и из
    // поля ввода (а курсор в чате почти всегда там) Escape до окна иначе не доходил.
    target.addEventListener?.('keydown', onKey, true);
    return () => target.removeEventListener?.('keydown', onKey, true);
  }, []);
}
